import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Sparkles } from 'lucide-react';

/** Apenas 4 descontos — o giro sempre para em R$ 5 (índice 0) */
export const ROULETTE_SEGMENTS = [
  { label: 'R$ 5', value: 5, color: '#db2777', color2: '#f472b6' },
  { label: 'R$ 10', value: 10, color: '#9d174d', color2: '#ec4899' },
  { label: 'R$ 2', value: 2, color: '#f9a8d4', color2: '#fce7f3' },
  { label: 'R$ 20', value: 20, color: '#be185d', color2: '#f472b6' },
];

const WIN_INDEX = 0;
const WIN_VALUE = 5;
const SPIN_MS = 4500;

function getAudioCtx(ref) {
  if (!ref.current) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ref.current = new AC();
  }
  if (ref.current.state === 'suspended') {
    ref.current.resume().catch(() => {});
  }
  return ref.current;
}

function playTick(ctx) {
  if (!ctx) return;
  const t = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'square';
  osc.frequency.setValueAtTime(920 + Math.random() * 180, t);
  gain.gain.setValueAtTime(0.07, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t);
  osc.stop(t + 0.045);
}

function playWin(ctx) {
  if (!ctx) return;
  const notes = [523.25, 659.25, 783.99, 1046.5];
  notes.forEach((freq, i) => {
    const t = ctx.currentTime + i * 0.11;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(0.18, t + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.55);
  });
}

function playWhoosh(ctx) {
  if (!ctx) return;
  const t = ctx.currentTime;
  const bufferSize = ctx.sampleRate * 0.25;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i += 1) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.setValueAtTime(400, t);
  filter.frequency.exponentialRampToValueAtTime(1800, t + 0.2);
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.12, t);
  gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
  noise.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  noise.start(t);
  noise.stop(t + 0.25);
}

/**
 * Roleta da presell — visual aprimorado + sons.
 * Sempre cai em R$ 5.
 */
const DiscountRoulette = ({ isOpen, onClose, onWon, dark = false }) => {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [won, setWon] = useState(false);
  const audioRef = useRef(null);
  const tickTimersRef = useRef([]);

  const n = ROULETTE_SEGMENTS.length;
  const segmentAngle = 360 / n;

  const conic = useMemo(() => {
    const stops = ROULETTE_SEGMENTS.map((seg, i) => {
      const start = i * segmentAngle;
      const end = start + segmentAngle;
      return `${seg.color} ${start}deg ${end}deg`;
    });
    return `conic-gradient(from 0deg, ${stops.join(', ')})`;
  }, [segmentAngle]);

  useEffect(() => {
    if (isOpen) {
      setSpinning(false);
      setWon(false);
      setRotation(0);
    }
    return () => {
      tickTimersRef.current.forEach((id) => clearTimeout(id));
      tickTimersRef.current = [];
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const card = dark
    ? 'bg-gradient-to-b from-zinc-900 to-zinc-950 border-pink-500/30 text-white'
    : 'bg-gradient-to-b from-white to-pink-50 border-pink-200 text-zinc-800';
  const muted = dark ? 'text-zinc-400' : 'text-zinc-500';

  const clearTicks = () => {
    tickTimersRef.current.forEach((id) => clearTimeout(id));
    tickTimersRef.current = [];
  };

  const scheduleTicks = (ctx) => {
    clearTicks();
    let elapsed = 0;
    const step = () => {
      if (elapsed >= SPIN_MS - 80) return;
      playTick(ctx);
      const progress = elapsed / SPIN_MS;
      // Começa rápido e desacelera (sensação de roleta)
      const delay = 35 + progress * progress * 320;
      elapsed += delay;
      const id = window.setTimeout(step, delay);
      tickTimersRef.current.push(id);
    };
    step();
  };

  const spin = () => {
    if (spinning || won) return;
    const ctx = getAudioCtx(audioRef);
    playWhoosh(ctx);
    setSpinning(true);

    const centerFromTop = WIN_INDEX * segmentAngle + segmentAngle / 2;
    const extraSpins = 7;
    const finalRotation = extraSpins * 360 + (360 - centerFromTop);
    setRotation(finalRotation);

    scheduleTicks(ctx);

    window.setTimeout(() => {
      clearTicks();
      setSpinning(false);
      setWon(true);
      playWin(ctx);
    }, SPIN_MS);
  };

  const claim = () => {
    onWon?.(WIN_VALUE);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-4 backdrop-blur-[2px] sm:items-center">
      <div
        className={`relative w-full max-w-md overflow-hidden rounded-3xl border p-6 shadow-2xl ${card}`}
      >
        {/* Brilho decorativo */}
        <div
          className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-pink-400/20 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-20 -left-10 h-40 w-40 rounded-full bg-rose-500/15 blur-3xl"
          aria-hidden
        />

        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 rounded-full bg-black/5 p-1.5 text-zinc-400 transition hover:bg-black/10 hover:text-zinc-600"
          aria-label="Fechar"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="relative mb-1 text-center">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-pink-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-pink-600">
            <Sparkles className="h-3.5 w-3.5" />
            Oferta especial
          </div>
          <h2 className="text-2xl font-extrabold tracking-tight text-pink-600">
            Roleta da sorte
          </h2>
          <p className={`mt-1.5 text-sm ${muted}`}>
            {won
              ? 'Parabéns! Você ganhou R$ 5 de desconto.'
              : 'Gire a roleta e ganhe um desconto agora!'}
          </p>
        </div>

        <div className="relative mx-auto my-8 h-[280px] w-[280px]">
          {/* Aura / glow */}
          <div
            className={`absolute inset-2 rounded-full ${
              spinning ? 'animate-pulse bg-pink-400/30' : 'bg-pink-300/20'
            } blur-xl`}
            aria-hidden
          />

          {/* Aro externo dourado/rosa */}
          <div
            className="absolute inset-0 rounded-full p-[6px]"
            style={{
              background:
                'conic-gradient(from 0deg, #fbbf24, #f472b6, #fbbf24, #ec4899, #fbbf24)',
              boxShadow:
                '0 8px 32px rgba(236, 72, 153, 0.35), inset 0 1px 0 rgba(255,255,255,0.5)',
            }}
          >
            <div
              className={`h-full w-full rounded-full ${dark ? 'bg-zinc-950' : 'bg-pink-50'} p-[5px]`}
            >
              {/* Disco */}
              <div
                className="relative h-full w-full overflow-hidden rounded-full"
                style={{
                  background: conic,
                  transform: `rotate(${rotation}deg)`,
                  transition: spinning
                    ? `transform ${SPIN_MS}ms cubic-bezier(0.12, 0.8, 0.05, 1)`
                    : 'none',
                  boxShadow: 'inset 0 0 24px rgba(0,0,0,0.25)',
                }}
              >
                {/* Divisórias */}
                {ROULETTE_SEGMENTS.map((_, i) => (
                  <div
                    key={`div-${i}`}
                    className="absolute left-1/2 top-1/2 h-1/2 w-[2px] origin-bottom bg-white/40"
                    style={{
                      transform: `translateX(-50%) rotate(${i * segmentAngle}deg)`,
                    }}
                  />
                ))}

                {ROULETTE_SEGMENTS.map((seg, i) => {
                  const mid = i * segmentAngle + segmentAngle / 2;
                  return (
                    <span
                      key={seg.label}
                      className="pointer-events-none absolute left-1/2 top-1/2 text-base font-extrabold tracking-tight text-white"
                      style={{
                        transform: `rotate(${mid}deg) translateY(-78px) rotate(${-mid}deg)`,
                        marginLeft: '-1.6rem',
                        width: '3.2rem',
                        textAlign: 'center',
                        textShadow: '0 2px 4px rgba(0,0,0,0.45)',
                      }}
                    >
                      {seg.label}
                    </span>
                  );
                })}

                {/* Brilho no disco */}
                <div
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(255,255,255,0.35) 0%, transparent 42%, transparent 58%, rgba(0,0,0,0.12) 100%)',
                  }}
                  aria-hidden
                />

                {/* Centro */}
                <div
                  className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border-[3px] border-amber-200 text-[10px] font-black uppercase tracking-wider text-pink-700"
                  style={{
                    background:
                      'radial-gradient(circle at 35% 30%, #ffffff, #fce7f3 55%, #f9a8d4)',
                    boxShadow:
                      '0 4px 14px rgba(190, 24, 93, 0.4), inset 0 2px 4px rgba(255,255,255,0.8)',
                  }}
                >
                  Giro
                </div>
              </div>
            </div>
          </div>

          {/* Ponteiro 3D */}
          <div className="absolute left-1/2 top-[-6px] z-20 -translate-x-1/2">
            <div
              className="relative"
              style={{
                filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.35))',
              }}
            >
              <div
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '14px solid transparent',
                  borderRight: '14px solid transparent',
                  borderTop: '28px solid #fbbf24',
                }}
              />
              <div
                className="absolute left-1/2 top-0 -translate-x-1/2"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '16px solid #fef3c7',
                }}
              />
              <div className="absolute left-1/2 top-[-4px] h-3 w-3 -translate-x-1/2 rounded-full border-2 border-amber-200 bg-amber-400" />
            </div>
          </div>

          {/* Confetes leves no win */}
          {won && (
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-full" aria-hidden>
              {[...Array(12)].map((_, i) => (
                <span
                  key={i}
                  className="absolute h-2 w-2 rounded-sm"
                  style={{
                    left: `${10 + (i * 7) % 80}%`,
                    top: `${15 + (i * 11) % 70}%`,
                    background: i % 2 === 0 ? '#fbbf24' : '#ec4899',
                    opacity: 0.85,
                    animation: `roulette-pop 0.8s ease-out ${i * 0.05}s both`,
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <style>{`
          @keyframes roulette-pop {
            0% { transform: scale(0) translateY(0); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: scale(1) translateY(-24px); opacity: 0; }
          }
        `}</style>

        {!won ? (
          <button
            type="button"
            onClick={spin}
            disabled={spinning}
            className={`relative w-full overflow-hidden rounded-2xl py-4 text-base font-bold text-white transition ${
              spinning
                ? 'cursor-wait bg-pink-300'
                : 'bg-gradient-to-r from-pink-600 via-rose-500 to-pink-500 shadow-lg shadow-pink-500/40 hover:brightness-110 active:scale-[0.98]'
            }`}
          >
            {!spinning && (
              <span
                className="pointer-events-none absolute inset-0 opacity-40"
                style={{
                  background:
                    'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%)',
                }}
              />
            )}
            {spinning ? 'Girando...' : 'Girar roleta'}
          </button>
        ) : (
          <div className="space-y-3">
            <div className="rounded-2xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-center dark:bg-amber-500/10">
              <p className="text-lg font-extrabold text-amber-600">− R$ 5,00</p>
              <p className={`text-xs ${muted}`}>Desconto liberado nos pacotes</p>
            </div>
            <button
              type="button"
              onClick={claim}
              className="w-full rounded-2xl bg-gradient-to-r from-pink-600 to-rose-500 py-4 text-base font-bold text-white shadow-lg shadow-pink-500/40 hover:brightness-110 active:scale-[0.98]"
            >
              Usar desconto de R$ 5
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscountRoulette;
